import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardContent, Dialog, DialogContent, Grid } from '@mui/material'
import { compact, isNil, uniq } from 'lodash'
import moment from 'moment'
import { CardTable, SORT } from 'common/components/datatable/Table'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import Autocomplete from 'common/components/form/Autocomplete'
import DateTimePicker from 'common/components/form/DateTimePicker'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { CardTitle } from 'common/components/card/Card'
import { getDate } from 'common/utils/DateUtil'
import { darkBlue } from 'app/theme'
import { formatRow } from '../CustomerTicketsApp'
import { getCustomerItems, LOT_STATUS_ITEMS } from './LotsUtils'
import { StatusChip } from './LotsCommon'

const NOTE_HEADERS = [
    { key: 'date', value: 'Date' },
    { key: 'statut', value: 'Statut' },
    { key: 'avancement', value: '%' },
    { key: 'commentaire', value: 'Commentaire' },
]

const TICKET_HEADERS = [
    { key: 'id', value: 'Id' },
    { key: 'avancement', value: '%' },
    { key: 'summary', value: 'Titre' },
    { key: 'project', value: 'Projet' },
    { key: 'status', value: 'Statut' },
    { key: 'category', value: 'Catégorie' },
    { key: 'dateSubmitted', value: 'Date de création' },
    { key: 'echeance', value: 'Échéance' },
    { key: 'reporter', value: 'Rapporteur' },
    { key: 'handler', value: 'Affecté à' },
]

// Dialog lot : champs, 1re note (création) ou tableau des notes (modification), tickets.
const LotDialog = ({ lot, notes = [], idClient, defaultStatut, customers = [], chiefItems = [], getDefaultChief = () => {}, allTickets = [], onAddNote = () => {}, onEditNote = () => {}, onClose, onSave }) => {
    const [form, setForm] = useState(lot ?? { tickets: [], idClient, idChefDeProjet: getDefaultChief(idClient) })
    const [firstNote, setFirstNote] = useState({ statut: defaultStatut, date: moment().valueOf(), avancement: 0 })
    const changeFirstNote = (changes) => setFirstNote(n => ({ ...n, ...changes }))
    const [selectedTicket, setSelectedTicket] = useState(undefined)
    const [importLot, setImportLot] = useState(undefined)
    const [isSaving, setIsSaving] = useState(false)
    const change = (changes) => setForm(f => ({ ...f, ...changes }))
    const addTickets = (ids) => change({ tickets: uniq([...(form.tickets ?? []), ...ids]) })

    const isEdit = !isNil(form.id)
    const isClientSelectable = !isEdit && isNil(idClient)
    const clientItems = useMemo(() => getCustomerItems(customers), [customers])

    const ticketById = useMemo(() => new Map(allTickets.map(t => [t.id, t])), [allTickets])
    const devLotItems = useMemo(() => uniq(compact(allTickets.map(t => t.lot))).sort().map(l => ({ id: l, label: l })), [allTickets])
    const ticketsByLot = useMemo(() => {
        const map = {}
        allTickets.forEach(t => {
            if (t.lot) {
                map[t.lot] = map[t.lot] ?? []; map[t.lot].push(t.id)
            }
        })
        return map
    }, [allTickets])
    const ticketItems = useMemo(() => allTickets.map(t => ({ id: t.id, label: `#${t.id} — ${t.summary ?? ''}` })), [allTickets])

    const noteRows = notes.map(note => ({
        noteRef: note,
        date: { value: getDate(note.date), sortValue: note.date },
        statut: { value: <StatusChip statut={note.statut} />, sortValue: note.statut },
        avancement: { value: `${note.avancement}%`, sortValue: note.avancement },
        commentaire: { value: note.commentaire ?? '' },
    }))

    const ticketRows = (form.tickets ?? []).map(ticketId => {
        const t = ticketById.get(ticketId) ?? { id: ticketId, summary: '' }
        return { ...formatRow(t), project: { value: t.project ?? '' } }
    })

    return (
        <Dialog open maxWidth='xl' fullWidth>
            <BasicDialogTitle onClose={onClose}>{isEdit ? 'Modifier le lot' : 'Nouveau lot'}</BasicDialogTitle>
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' sx={{ paddingTop: 1 }}>
                            {isClientSelectable && (
                                <Grid size={12}>
                                    <Autocomplete label='Client' value={form.idClient} items={clientItems} onChange={v => change({ idClient: v, idChefDeProjet: getDefaultChief(v) })} sortItems={false} required />
                                </Grid>
                            )}
                            <Grid size={6}>
                                <Input label='Nom' value={form.nom} onChange={v => change({ nom: v })} required />
                            </Grid>
                            <Grid size={3}>
                                <Autocomplete label='Chef de projet' value={form.idChefDeProjet} items={chiefItems} onChange={v => change({ idChefDeProjet: v })} sortItems={false} />
                            </Grid>
                            <Grid size={3}>
                                <DateTimePicker label='Échéance' value={form.echeance} onChange={v => change({ echeance: v })} required />
                            </Grid>
                            <Grid size={4}>
                                <Input label='Total (HT)' type='float' value={form.total} onChange={v => change({ total: isNil(v) || v === '' ? undefined : Number(v) })} />
                            </Grid>
                            <Grid size={4}>
                                <Input label='Devis' value={form.devis} onChange={v => change({ devis: v })} />
                            </Grid>
                            <Grid size={4}>
                                <Input label='Bon de commande' value={form.bonCommande} onChange={v => change({ bonCommande: v })} />
                            </Grid>
                            <Grid size={12}>
                                <Input label='Commentaire' value={form.commentaire} onChange={v => change({ commentaire: v })} multiline />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                {isEdit && (
                    <Box sx={{ marginTop: 1 }}>
                        <CardTable
                            title={`Notes d'avancement (${noteRows.length})`}
                            color='#161832'
                            hideNbElements
                            rows={noteRows}
                            headers={NOTE_HEADERS}
                            rowsPerPageDefault={25}
                            defaultSort={{ column: 'date', direction: SORT.DESC }}
                            actions={[{
                                icon: 'add_comment',
                                tooltip: 'Ajouter une note',
                                onClick: () => onAddNote(lot),
                            }]}
                            lineActions={[{
                                icon: 'edit',
                                tooltip: 'Modifier',
                                onClick: (row) => onEditNote(lot, row.noteRef),
                            }]}
                        />
                    </Box>
                )}
                {!isEdit && (
                    <Card sx={{ marginTop: 1 }}>
                        <CardTitle title='Première note' color={darkBlue} />
                        <CardContent>
                            <Grid container columnSpacing={2} rowSpacing={1} alignItems='center'>
                                <Grid size={4}>
                                    <Select label='Statut' items={LOT_STATUS_ITEMS} value={firstNote.statut} onChange={v => changeFirstNote({ statut: v })} sortItems={false} />
                                </Grid>
                                <Grid size={4}>
                                    <DateTimePicker label='Date' value={firstNote.date} onChange={v => changeFirstNote({ date: v })} />
                                </Grid>
                                <Grid size={4}>
                                    <Input label='Avancement (%)' type='number' value={firstNote.avancement} onChange={v => changeFirstNote({ avancement: isNil(v) || v === '' ? undefined : Number(v) })} required />
                                </Grid>
                                <Grid size={12}>
                                    <Input label='Commentaire' value={firstNote.commentaire} onChange={v => changeFirstNote({ commentaire: v })} multiline />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}
                <Card sx={{ marginTop: 1 }}>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center'>
                            <Grid size={6}>
                                <Autocomplete
                                    label='Ajouter un ticket'
                                    items={ticketItems}
                                    value={selectedTicket}
                                    onChange={(v) => {
                                        if (!isNil(v)) {
                                            addTickets([v]); setSelectedTicket(undefined)
                                        }
                                    }}
                                    sortItems={false}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Autocomplete
                                    label='Importer un lot de dev'
                                    items={devLotItems}
                                    value={importLot}
                                    onChange={(lotName) => {
                                        setImportLot(lotName); addTickets(ticketsByLot[lotName] ?? [])
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <CardTable
                                    title={`Tickets du lot (${ticketRows.length})`}
                                    color='#161832'
                                    hideNbElements
                                    rows={ticketRows}
                                    headers={TICKET_HEADERS}
                                    rowsPerPageDefault={25}
                                    defaultSort={{ column: 'id', direction: SORT.ASC }}
                                    actions={[{
                                        icon: 'delete_sweep',
                                        tooltip: 'Tout retirer',
                                        displayed: (form.tickets ?? []).length > 0,
                                        onClick: () => change({ tickets: [] }),
                                    }]}
                                    lineActions={[{
                                        icon: 'delete',
                                        color: 'red',
                                        tooltip: 'Retirer',
                                        onClick: (row) => change({ tickets: (form.tickets ?? []).filter(x => x !== row.id) }),
                                    }]}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isEdit ? 'Modifier' : 'Créer'}
                onValidate={() => {
                    setIsSaving(true)
                    onSave({ ...form, tickets: form.tickets ?? [], notes: isEdit ? form.notes : [firstNote] }).finally(() => setIsSaving(false))
                }}
                isLoading={isSaving}
                isDisabled={!form.nom || isNil(form.echeance) || isNil(form.idClient) || (!isEdit && (isNil(firstNote.avancement) || isNil(firstNote.date)))}
            />
        </Dialog>
    )
}
LotDialog.propTypes = {
    lot: PropTypes.shape({}), notes: PropTypes.arrayOf(PropTypes.shape({})), idClient: PropTypes.number, defaultStatut: PropTypes.number, customers: PropTypes.arrayOf(PropTypes.shape({})),
    chiefItems: PropTypes.arrayOf(PropTypes.shape({})), getDefaultChief: PropTypes.func, allTickets: PropTypes.arrayOf(PropTypes.shape({})),
    onAddNote: PropTypes.func, onEditNote: PropTypes.func, onClose: PropTypes.func, onSave: PropTypes.func,
}

export default LotDialog
